
import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  Mail, 
  CreditCard, 
  MessagesSquare, 
  Facebook, 
  Instagram, 
  Video, 
  CheckCircle, 
  Info, 
  ExternalLink, 
  ArrowRight
} from 'lucide-react';
import { 
  Accordion, 
  AccordionContent, 
  AccordionItem, 
  AccordionTrigger 
} from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import Footer from '@/components/layout/Footer';
import Navbar from '@/components/layout/Navbar';

// Platform data structure
const platforms = [
  {
    id: "google-platforms",
    title: "Google Platforms",
    icon: <div className="h-6 w-6 flex items-center justify-center bg-blue-500 rounded-full text-white font-bold text-xs">G</div>,
    description: "Access to your Google platforms allows us to set up automations across Gmail, Calendar, YouTube, and other Google services.",
    steps: [
      "You'll receive an email invitation from ThinkTomorrow",
      "Click the link in the email to grant access",
      "Sign in to your Google account",
      "Review and approve the permissions (we need full access to build automations)",
      "That's it! We'll handle the rest"
    ]
  },
  {
    id: "microsoft-outlook",
    title: "Microsoft Outlook & Calendar",
    icon: <div className="h-6 w-6 flex items-center justify-center bg-blue-500 rounded-full text-white font-bold text-xs">M</div>,
    description: "Outlook access helps us integrate with your Microsoft ecosystem for seamless communication.",
    steps: [
      "Sign in to your Outlook.com or Microsoft 365 account",
      "Go to Settings > Calendar > Shared calendars",
      'Under "Share a calendar", choose your calendar',
      'Enter hello@thinktomorrow.ai and choose "Can view, edit, and delete" permission (highest level)',
      'Click "Share" to send the invitation'
    ]
  },
  {
    id: "xero",
    title: "Xero",
    icon: <div className="h-6 w-6 flex items-center justify-center bg-[#13B5EA] rounded-full text-white font-bold text-xs">X</div>,
    description: "Xero access allows us to set up automated invoicing and payment tracking for your services.",
    steps: [
      "Log in to your Xero account",
      "Go to Settings > Users",
      'Click "Invite a user"',
      "Enter hello@thinktomorrow.ai",
      'Select "Financial Admin" user type (highest access level)',
      "Set all permissions to full access",
      'Click "Send invitation"'
    ],
    hasForm: true,
    formType: "input",
    formLabel: "Your Xero Organization ID (optional)",
    formPlaceholder: "Enter your Xero Organization ID if you have it"
  },
  {
    id: "stripe",
    title: "Stripe",
    icon: <CreditCard className="h-6 w-6 text-purple-500" />,
    description: "Stripe integration lets us set up and manage payment processing for your services.",
    steps: [
      "Log in to your Stripe Dashboard",
      "Go to Settings > Team",
      'Click "Invite"',
      "Enter hello@thinktomorrow.ai",
      'Select "Administrator" role (highest access level)',
      'Click "Send invitation"'
    ],
    hasForm: true,
    formType: "input",
    formLabel: "Your Stripe Account ID (optional)",
    formPlaceholder: "Enter your Stripe Account ID if you have it"
  },
  {
    id: "meta",
    title: "Meta (Facebook & Instagram)",
    icon: <div className="flex gap-1">
      <Facebook className="h-6 w-6 text-blue-600" />
      <Instagram className="h-6 w-6 text-pink-500" />
    </div>,
    description: "Partner access allows us to manage your social media presence across Meta platforms.",
    steps: [
      "Log in to Facebook Business Suite",
      "Go to Settings > Partners",
      'Click "Add Partners"',
      "Enter our Business ID: 4102754556714006",
      'Set permission level to "Admin" (highest level)',
      'Click "Confirm"'
    ],
    videoId: "ABCDEFG"
  },
  {
    id: "tiktok",
    title: "TikTok (Optional)",
    icon: <Video className="h-6 w-6 text-black" />,
    description: "TikTok access is needed if you want us to manage your TikTok content and campaigns.",
    steps: [
      "Log in to your TikTok account",
      "Go to Settings > Privacy > Manage Account",
      'Select "Business Account" if not already set',
      "Go to Business Center > Users > Invite",
      "Enter hello@thinktomorrow.ai",
      'Select "Admin" role (highest access level)',
      'Click "Send Invitation"'
    ],
    hasForm: true,
    formType: "textarea",
    formLabel: "Additional TikTok Information (optional)",
    formPlaceholder: "Any specific information about your TikTok account or goals?"
  }
];

const SupportHub: React.FC = () => {
  const [completedItems, setCompletedItems] = useState<string[]>([]);

  const toggleCompleted = (id: string) => {
    if (completedItems.includes(id)) {
      setCompletedItems(completedItems.filter(item => item !== id));
    } else {
      setCompletedItems([...completedItems, id]);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-16">  {/* Added pt-16 to account for fixed navbar */}
      <Helmet>
        <title>Support Hub | Platform Access & Setup | ThinkTomorrow</title>
        <meta name="description" content="Securely connect your platforms with ThinkTomorrow. Step-by-step access guides for Google, Meta, TikTok, and other tools we manage on your behalf." />
        <link rel="canonical" href="https://thinktomorrow.ai/help" />
        <meta property="og:title" content="Support Hub | ThinkTomorrow" />
        <meta property="og:description" content="Step-by-step platform access guides for ThinkTomorrow clients." />
        <meta property="og:url" content="https://thinktomorrow.ai/help" />
        <meta property="og:type" content="website" />
      </Helmet>
      <Navbar />

      <div className="bg-brand-navy text-white py-6 px-4">  {/* Reduced py from 8 to 6 */}
        <div className="max-w-5xl mx-auto text-center">
          <h1 className="text-3xl font-bold mb-4">Support Hub – Platform Access & Setup</h1>
          <p className="text-xl font-light max-w-3xl mx-auto">
            Welcome to the ThinkTomorrow Support Hub — here you'll find everything needed to securely connect your platforms so we can get started. If you need help, contact us directly.
          </p>
        </div>
      </div>
      
      <div className="max-w-5xl mx-auto py-12 px-4">
        <div className="bg-white rounded-lg shadow-md p-8 mb-8">
          <div className="flex items-center gap-3 mb-6">
            <Info className="h-5 w-5 text-brand-teal" />
            <h2 className="text-xl font-semibold">Getting Started</h2>
          </div>
          <p className="text-gray-700 mb-6">
            Please work through the sections below to provide the necessary access for our team. This will help us get your AI services up and running quickly. You don't need to complete all sections at once - you can always come back to this page later.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 mb-8">
            <Card className="flex-1 border-l-4 border-l-green-500">
              <CardContent className="p-4">
                <h3 className="font-medium text-green-700 mb-2">Secure Access</h3>
                <p className="text-sm text-gray-600">
                  We use industry-standard security practices and only request the minimum access required.
                </p>
              </CardContent>
            </Card>
            <Card className="flex-1 border-l-4 border-l-blue-500">
              <CardContent className="p-4">
                <h3 className="font-medium text-blue-700 mb-2">Quick Setup</h3>
                <p className="text-sm text-gray-600">
                  Most platforms take just 2-3 minutes to connect following our simple instructions.
                </p>
              </CardContent>
            </Card>
            <Card className="flex-1 border-l-4 border-l-purple-500">
              <CardContent className="p-4">
                <h3 className="font-medium text-purple-700 mb-2">Full Control</h3>
                <p className="text-sm text-gray-600">
                  You maintain full control and can revoke access at any time through your accounts.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
        
        <div>
          <h2 className="text-2xl font-bold mb-6">Platform Connections</h2>
          <p className="text-gray-700 mb-8">
            Expand each section below and follow the step-by-step instructions to connect your platforms.
          </p>
          
          <Accordion type="single" collapsible className="space-y-4">
            {platforms.map(platform => (
              <AccordionItem 
                key={platform.id}
                value={platform.id}
                className={`border bg-white rounded-lg overflow-hidden ${completedItems.includes(platform.id) ? 'border-green-200 bg-green-50' : 'border-gray-200'}`}
              >
                <AccordionTrigger className="px-6 py-4 hover:no-underline">
                  <div className="flex items-center gap-3 text-left">
                    <div className="flex-shrink-0">
                      {platform.icon}
                    </div>
                    <div className="flex-grow">
                      <h3 className="text-lg font-medium">{platform.title}</h3>
                      <p className="text-sm text-gray-500 mt-1">{platform.description}</p>
                    </div>
                    {completedItems.includes(platform.id) && (
                      <div className="flex-shrink-0 mr-4">
                        <CheckCircle className="h-5 w-5 text-green-500" />
                      </div>
                    )}
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-6 py-4 border-t">
                  <div className="space-y-6">
                    <div>
                      <h4 className="font-medium mb-2 flex items-center">
                        <span className="flex-shrink-0 inline-flex items-center justify-center w-6 h-6 rounded-full bg-brand-teal text-white text-xs mr-2">1</span>
                        Follow these steps:
                      </h4>
                      <ul className="list-disc pl-8 space-y-2 text-gray-700">
                        {platform.steps.map((step, index) => (
                          <li key={index}>{step}</li>
                        ))}
                      </ul>
                    </div>
                    
                    {platform.videoId && (
                      <div>
                        <h4 className="font-medium mb-2 flex items-center">
                          <span className="flex-shrink-0 inline-flex items-center justify-center w-6 h-6 rounded-full bg-brand-teal text-white text-xs mr-2">2</span>
                          Video Tutorial:
                        </h4>
                        <div className="border rounded-md overflow-hidden">
                          <div className="bg-gray-100 p-4 rounded-md flex items-center justify-between">
                            <span className="text-gray-700">Watch our step-by-step guide</span>
                            <Button variant="outline" size="sm" className="text-xs">
                              <ExternalLink className="h-3 w-3 mr-1" />
                              Watch Tutorial
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {platform.hasForm && (
                      <div>
                        <h4 className="font-medium mb-2 flex items-center">
                          <span className="flex-shrink-0 inline-flex items-center justify-center w-6 h-6 rounded-full bg-brand-teal text-white text-xs mr-2">{platform.videoId ? '3' : '2'}</span>
                          Additional Information:
                        </h4>
                        <div className="space-y-2">
                          <Label htmlFor={`form-${platform.id}`}>{platform.formLabel}</Label>
                          {platform.formType === "input" ? (
                            <Input id={`form-${platform.id}`} placeholder={platform.formPlaceholder} className="max-w-md" />
                          ) : (
                            <Textarea id={`form-${platform.id}`} placeholder={platform.formPlaceholder} className="max-w-md" />
                          )}
                        </div>
                      </div>
                    )}
                    
                    <div className="pt-4 border-t">
                      <div className="flex items-center">
                        <Checkbox 
                          id={`completed-${platform.id}`}
                          checked={completedItems.includes(platform.id)}
                          onCheckedChange={() => toggleCompleted(platform.id)}
                          className="mr-2"
                        />
                        <Label 
                          htmlFor={`completed-${platform.id}`}
                          className="font-medium text-gray-700"
                        >
                          I've connected this platform
                        </Label>
                      </div>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
        
        <div className="mt-12 bg-white rounded-lg shadow-md p-8">
          <h2 className="text-2xl font-bold mb-4">Next Steps</h2>
          <p className="text-gray-700 mb-6">
            Once you've completed the necessary connections, our team will be notified and will begin setting up your services.
          </p>
          
          <div className="flex justify-between items-center">
            <div>
              <p className="text-sm text-gray-500">
                Platforms connected: {completedItems.length} of {platforms.length}
              </p>
            </div>
            <Button className="bg-brand-teal hover:bg-brand-teal/90">
              Notify Our Team <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default SupportHub;
